import { useQuery, useMutation } from '@tanstack/react-query';

import Axios from 'axios';
import { queryClient } from 'QueryClientProvider';

const getContactPayments = (ID) => {
    return Axios.get(`/payments/contact/${ID}`);
};

const usePayments = (ID) => {
    return useQuery({
        queryKey: ['payments-contact', ID],
        queryFn: () => getContactPayments(ID),
        enabled: true,
        refetchOnWindowFocus: false,
        staleTime: 300000,
        cacheTime: Infinity,
        retry: false,
    });
};

const forceReloadPayments = (ID) => queryClient.invalidateQueries({ queryKey: ['payments-contact', ID] });

const getPayment = (ID) => {
    return Axios.get(`/payments/${ID}`);
};

const getNw3Payment = (ID) => {
    return Axios.get(`/nw3payments/${ID}`);
};

const usePayment = (ID) => {
    return useQuery({
        queryKey: ['payments', ID],
        queryFn: () => getPayment(ID),
        staleTime: 10000,
        cacheTime: 10000,
        retry: false,
        enabled: ID ? true : false,
    });
};

const useNw3Payment = (ID) => {
    return useQuery({
        queryKey: ['nw3payments', ID],
        queryFn: () => getNw3Payment(ID),
        staleTime: 10000,
        cacheTime: 10000,
        retry: false,
        enabled: ID ? true : false,
    });
};

const forceReloadPayment = (ID) => queryClient.invalidateQueries({ queryKey: ['payments', ID] });

const getPaymentsSearchParams = (filters) => {
    if (filters) {
        let searchParams = { ...filters };
        if (filters.Period) {
            searchParams = {
                ...searchParams,
                FromDate: filters.Period[0],
                ToDate: filters.Period[1],
            };
            delete searchParams.Period;
        }
        return searchParams;
    }
    return null;
};

const onSearchPayments = (searchParams) => {
    return Axios.post('/Payments/Search', searchParams);
};

const useSearchPayments = (filters) => {
    const searchParams = getPaymentsSearchParams(filters);
    return useQuery({
        queryKey: ['payments-search'],
        queryFn: () => onSearchPayments(searchParams),
        enabled: true,
        refetchOnWindowFocus: false,
        staleTime: 300000,
        cacheTime: Infinity,
        retry: false,
    });
};

const refetchPaymentsSearch = async (filters) => {
    const searchParams = getPaymentsSearchParams(filters);
    try {
        await queryClient.fetchQuery({
            queryKey: ['payments-search'],
            queryFn: () => onSearchPayments(searchParams),
        });
    } catch (error) {
        console.error(error);
    }
};

const forceReloadSearchPayments = (area) => queryClient.invalidateQueries({ queryKey: ['payments-search', area] });

const onAddNewPayment = ({ data }) => {
    return Axios.post(`/payments`, data);
};

const useAddNewPayment = () => {
    return useMutation({
        mutationFn: onAddNewPayment,
        onSuccess: () => {},
    });
};

const onUpdatePayment = ({ data }) => {
    return Axios.put(`/payments`, data);
};

const useUpdatePayment = () => {
    return useMutation({
        mutationFn: onUpdatePayment,
        onSuccess: (_, variables) => {
            forceReloadPayment(variables.data.ID);
        },
    });
};

const onUpdatePaymentNumber = ({ data }) => {
    return Axios.put(`/payments/updatenumberanddate`, data);
};

const useUpdatePaymentNumber = () => {
    return useMutation({
        mutationFn: onUpdatePaymentNumber,
        onSuccess: (_, variables) => {
            forceReloadPayment(variables.data.ID);
        },
    });
};

const onGroupRows = ({ ID, data }) => {
    return Axios.put(`/payments/statements/group/${ID}`, data);
};

const useGroupRows = () => {
    return useMutation({
        mutationFn: onGroupRows,
        onSuccess: (_, variables) => {
            forceReloadPayment(variables.ID);
        },
    });
};

const onUnGroupRows = ({ ID, data }) => {
    return Axios.put(`/payments/statements/group/${ID}`, data);
};

const useUnGroupRows = () => {
    return useMutation({
        mutationFn: onUnGroupRows,
        onSuccess: (_, variables) => {
            forceReloadPayment(variables.ID);
        },
    });
};

const onDeleteRow = ({ data }) => {
    return Axios.delete(`/payments/${data.PaymentID}/statements/${data.StatementID}`, null);
};

const useDeleteRow = () => {
    return useMutation({
        mutationFn: onDeleteRow,
        onSuccess: (_, variables) => {
            forceReloadPayment(variables.data.PaymentID);
        },
    });
};

const onDeletePayment = (ID) => {
    return Axios.delete(`/payments/${ID}`);
};

const useDeletePayment = () => {
    return useMutation({
        mutationFn: onDeletePayment,
        onSuccess: () => {},
    });
};

export {
    usePayment,
    useNw3Payment,
    useAddNewPayment,
    useUpdatePayment,
    useUpdatePaymentNumber,
    useGroupRows,
    useUnGroupRows,
    useDeleteRow,
    useDeletePayment,
    useSearchPayments,
    usePayments,
    forceReloadPayments,
    forceReloadPayment,
    forceReloadSearchPayments,
    refetchPaymentsSearch,
};
